/**
 * `/home` — the learner dashboard (SCH-AI-2a, SCHOOLOGY_STUDENT_AI_PLAN
 * Slice 2a).
 *
 * Login lands HERE, on "your numbers and your next action", not a catalog:
 * the KPI row, the continue-learning card, due-soon quizzes, the score-trend
 * + projection card, and the past-attempts table — all from TWO queries
 * (`learning.learner.home`, the one aggregate DTO, + `…analytics.trend`).
 * All math is server-side zero-LLM core; this screen only formats it.
 *
 * This slice OWNS the dashboard layout. Later slices fill the named empty
 * slots rendered as chromeless `data-slot` divs (`mentor-rail`, `plan-card`,
 * `due-today`) — they're position markers, invisible until filled.
 *
 * Gated by the `learnerAnalyticsEnabled` vertical feature: the procs throw
 * FORBIDDEN when it's off, and this screen degrades by redirecting to
 * `/catalog` (the pre-2a landing) instead of erroring. Mobile-first: single
 * column at 390px, main-column + rail from `lg` up.
 */
import * as React from 'react';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { Compass, XCircle } from 'lucide-react';
import { Button, EmptyState, Skeleton } from '@mangrove/ui';
import { trpc } from '../lib/trpc.js';
import { RequireAuth, useCurrentUser } from '../lib/auth.js';
import { useResolvedFeatures } from '../lib/features.js';
import { LearnShell } from '../components/shell.js';
import { CourseFilter } from '../components/course-filter.js';
import { HomeKpiRow, HomeKpiRowSkeleton } from '../components/home/kpi-row.js';
import { ContinueLearningCard } from '../components/home/continue-learning-card.js';
import { DueSoonCard } from '../components/home/due-soon-card.js';
import { TrendCard } from '../components/home/trend-card.js';
import { PastAttemptsCard } from '../components/home/past-attempts-card.js';
import { HomeMasteryRadarCard } from '../components/home/mastery-mini-card.js';

export const Route = createFileRoute('/home')({
  component: HomePage,
});

function HomePage(): React.JSX.Element {
  return (
    <RequireAuth>
      <LearnShell title="Home" wide>
        <HomeContent />
      </LearnShell>
    </RequireAuth>
  );
}

function HomeContent(): React.JSX.Element {
  const me = useCurrentUser();
  const features = useResolvedFeatures();
  // Coaching-centre students have learnerAnalyticsEnabled=false → both procs
  // throw FORBIDDEN. GATE the queries on the resolved flag so we never fire a
  // known-forbidden request: an expected FORBIDDEN still surfaces as a console
  // error (and a wasted round-trip) before the redirect below. We only fetch
  // once analytics is KNOWN-enabled; the FORBIDDEN fall-through stays as a
  // backstop. (resolvedFeatures is already in cache from the shell/index.)
  const analyticsEnabled = features.data?.learnerAnalyticsEnabled === true;
  const [courseId, setCourseId] = React.useState('');
  const scope = courseId ? { courseId } : undefined;
  const homeQ = trpc.learning.learner.home.useQuery(scope, { enabled: analyticsEnabled });
  const trendQ = trpc.learning.learner.analytics.trend.useQuery(scope, {
    enabled: analyticsEnabled,
  });

  // ALL hooks must run before any conditional return (React hooks rule) — the
  // FORBIDDEN fall-through below is an early return, so every hook (incl. this
  // useMemo) is called first. A coaching-centre student has
  // learnerAnalyticsEnabled=false → both procs FORBIDDEN → this path fires every
  // render; with the useMemo after the return it crashed "fewer hooks than expected".
  const firstName = me.data?.name?.trim().split(/\s+/)[0];
  const today = React.useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }),
    [],
  );

  // Flag off (learnerAnalyticsEnabled=false) ⇒ no learner dashboard. Once the
  // resolved flags load and analytics is off, send the student to their course
  // catalog (their effective home) — before the gated queries below would have
  // fired. The FORBIDDEN check is a backstop for the rare case a proc rejects
  // while the flag still reads enabled.
  if (features.data && !analyticsEnabled) {
    return <Navigate to="/catalog" replace />;
  }
  if (homeQ.error?.data?.code === 'FORBIDDEN' || trendQ.error?.data?.code === 'FORBIDDEN') {
    return <Navigate to="/catalog" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
            Welcome back{firstName ? `, ${firstName}` : ''}
          </h2>
          <p className="text-sm text-muted-foreground">
            {today} <span className="text-muted-foreground/60">·</span> Your numbers and your next
            action.
          </p>
        </div>
        <CourseFilter value={courseId} onChange={setCourseId} className="sm:mt-0.5" />
      </div>

      {homeQ.isPending ? (
        <HomeSkeleton />
      ) : homeQ.isError ? (
        <ErrorCard
          message="We couldn't load your dashboard. Please check your connection and try again."
          onRetry={() => {
            void homeQ.refetch();
            void trendQ.refetch();
          }}
        />
      ) : (
        <>
          <HomeKpiRow kpis={homeQ.data.kpis} />

          {/* grid-cols-1 = minmax(0,1fr): an implicit `auto` track would size
              to the widest card's max-content and overflow narrow phones. */}
          <div className="ls-stagger grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="min-w-0 space-y-4 lg:col-span-2">
              {/* Slice 5+ slots — chromeless position markers until filled. */}
              <div data-slot="due-today" />
              {homeQ.data.continueLearning ? (
                <ContinueLearningCard data={homeQ.data.continueLearning} />
              ) : null}
              <div data-slot="plan-card" />
              <DueSoonCard quizzes={homeQ.data.dueSoon} />
              <PastAttemptsCard attempts={homeQ.data.pastAttempts} />
            </div>
            <div className="min-w-0 space-y-4">
              <TrendCard
                trend={trendQ.data}
                isPending={trendQ.isPending}
                isError={trendQ.isError}
                onRetry={() => void trendQ.refetch()}
              />
              <HomeMasteryRadarCard scope={scope} />
              <div data-slot="mentor-rail" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function HomeSkeleton(): React.JSX.Element {
  return (
    <div className="space-y-6">
      <HomeKpiRowSkeleton />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="min-w-0 space-y-4 lg:col-span-2">
          {/* continue hero */}
          <Skeleton className="h-40 w-full rounded-xl" />
          {/* due-soon + past attempts */}
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
        <div className="min-w-0 space-y-4">
          {/* trend + mastery radar */}
          <Skeleton className="h-56 w-full rounded-xl" />
          <Skeleton className="h-72 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}

function ErrorCard({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <EmptyState
      icon={XCircle}
      title="We couldn't load your dashboard"
      description={message}
      action={
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button variant="default" size="sm" className="min-h-touch" onClick={onRetry}>
            Try again
          </Button>
          <Button variant="ghost" size="sm" className="min-h-touch" asChild>
            <Link to="/catalog">
              <Compass className="size-4" aria-hidden="true" />
              Browse courses
            </Link>
          </Button>
        </div>
      }
    />
  );
}
